// Shared renderer boundary, mirrored byte-for-byte in the browser. Reject unknown
// fields at every level: no raw result, cost, margin, coefficient or audit object.
const TEMPLATE_VERSION = "ICC-QUOTE-B5-1";
const fail = () => { throw Object.assign(new Error("Projection documentaire v2 invalide ou non autorisée."),
  { code: "QUOTE_DOCUMENT_V2_INVALID", status: 422 }); };
const check = (condition) => { if (!condition) fail(); };
const string = value => check(typeof value === "string");
const nonempty = value => { string(value); check(value.trim().length > 0); };
const bool = value => check(typeof value === "boolean");
const integer = value => check(Number.isSafeInteger(value) && value >= 0);
const decimal = value => check(typeof value === "string" && /^\d+(?:\.\d+)?$/.test(value));
const money = value => check(typeof value === "string" && /^\d{1,12}\.\d{2}$/.test(value));
const nullable = fn => value => { if (value !== null) fn(value); };
const array = fn => value => { check(Array.isArray(value)); value.forEach(fn); };
const exact = (value, fields) => {
  check(value !== null && typeof value === "object" && !Array.isArray(value));
  check(Object.keys(value).length === Object.keys(fields).length && Object.keys(value).every(key => Object.hasOwn(fields, key)));
  Object.entries(fields).forEach(([key, fn]) => fn(value[key]));
};
const textFields = names => Object.fromEntries(names.split(" ").map(name => [name, string]));
const scalar = value => check(value === null || typeof value === "string" || (typeof value === "number" && Number.isFinite(value)));
const scheduleFields = "amountType fixedAmount percentage triggerType triggerDate phaseLabelSnapshot thresholdValue scopeType scopePhaseCode scopeLotCode label";
const commercialFields = "objectText scopeText exclusionsText notesClient paymentTerms executionDelay validUntil riskPreventionText";
const customerFields = "customerLegalName customerTradeName customerAddressLine1 customerAddressLine2 customerPostalCode customerCity customerCountry customerContactName customerContactEmail customerContactPhone";
const companyFields = { ...textFields("displayName legalName footerLegalText"), addressLines: array(string), legalLines: array(string),
  logoDataUrl: value => check(typeof value === "string" && /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+=*$/.test(value)) };
const tax = value => exact(value, { rate: decimal, base_ht: money, tax_amount: money, total_ttc: money });
const terms = value => {
  exact(value, { schemaVersion: v => check(v === 1), templateId: nonempty, validityDateLabel: nonempty,
    blockCount: integer, clauseCount: integer,
    commercialTerms: v => {
      const fields = { paymentTerms: string, riskPreventionText: string };
      if (Object.hasOwn(v || {}, "paymentScheduleNotice")) fields.paymentScheduleNotice = string;
      exact(v, fields);
    }, blocks: array(v => exact(v, { kind: k => check(["title", "heading", "paragraph", "item"].includes(k)), text: nonempty })) });
  check(value.blocks.length > 0 && value.blockCount === value.blocks.length
    && value.clauseCount === value.blocks.filter(b => b.kind === "heading").length);
};
const sectionTree = (sections) => {
  const sectionIds = new Set(), lineIds = new Set();
  const walk = items => array(section => {
    exact(section, { id: nonempty, code: string, title: nonempty, description: string, orderIndex: integer,
      totalHT: nullable(money), lines: array(() => {}), children: array(() => {}) });
    check(!sectionIds.has(section.id)); sectionIds.add(section.id);
    const parents = new Map();
    section.lines.forEach(line => {
      const fields = { id: nonempty, quoteSectionId: nonempty, parentLineId: nullable(nonempty), structureKind: v => check(["simple", "composite"].includes(v)),
        lineType: v => check(["service", "material", "subcontracting", "text", "subtotal"].includes(v)),
        code: string, description: string, lineComment: string, unit: string, localQuantity: decimal, effectiveQuantity: decimal,
        depth: integer, orderIndex: integer, includedInParent: bool, displayMode: v => check(["descriptions", "priced"].includes(v)) };
      if (Object.hasOwn(line, "prices")) fields.prices = v => exact(v, { totalHT: money, totalVat: money, totalTTC: money });
      exact(line, fields);
      check(!lineIds.has(line.id) && line.quoteSectionId === section.id); lineIds.add(line.id);
      const parent = line.parentLineId === null ? null : parents.get(line.parentLineId);
      check(line.parentLineId === null || parent?.structureKind === "composite");
      check(line.depth === (parent ? parent.depth + 1 : 0) && line.includedInParent === !!parent);
      const descriptive = ["text", "subtotal"].includes(line.lineType);
      const allowed = !descriptive && (!parent || (parent.pricesAllowed && parent.displayMode === "priced"));
      check(Object.hasOwn(line, "prices") === allowed);
      if (line.structureKind === "composite") check(!descriptive);
      parents.set(line.id, { ...line, pricesAllowed: allowed });
    });
    walk(section.children);
  })(items);
  walk(sections);
};
const contractTerms = value => exact(value, { deliveryFormat: string, siteVisitsIncluded: bool, siteVisitCount: scalar,
  siteVisitConditions: string, escalationFormula: nullable(string), revisionFormula: nullable(string), depositRate: decimal,
  paymentSchedule: array(v => exact(v, Object.fromEntries(scheduleFields.split(" ").map(key => [key, scalar])))) });
const assertClientDocumentV2 = document => {
  exact(document, { schemaVersion: v => check(v === 2), templateVersion: v => check(v === TEMPLATE_VERSION), currencyCode: v => check(v === "EUR"),
    identity: v => exact(v, { quoteId: nonempty, quoteVersionId: nonempty, quoteNumber: nonempty, versionNumber: integer,
      createdAt: nonempty, documentDate: nonempty, timeZone: nonempty, createdAtLabel: nonempty, documentDateTimeLabel: nonempty }),
    company: v => exact(v, companyFields), customer: v => exact(v, textFields(customerFields)),
    version: v => exact(v, { ...textFields(commercialFields), validityDays: integer }),
    facts: v => exact(v, { terms, sections: sectionTree, totalSellingPriceHT: money, totalVatAmount: money, totalSellingPriceTTC: money,
      taxBreakdown: array(tax), optionTotalsUnavailable: v => check(v === false), contractTerms,
      options: array(option => exact(option, { id: nonempty, title: nonempty, selected: bool, sections: sectionTree,
        totalHT: money, totalVat: money, totalTTC: money, taxBreakdown: array(tax) })) }) });
  const ids = document.facts.options.map(o => o.id);
  check(new Set(ids).size === ids.length);
  return document;
};
module.exports = { TEMPLATE_VERSION, assertClientDocumentV2, customerFields, scheduleFields, commercialFields };
